import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import { createNamedApi, combineEffectsWithProviders } from '@essent/common';
import * as i1$1 from '@ngrx/effects';
import { EffectsModule } from '@ngrx/effects';
import * as i1 from '@angular/common/http';

const API = new InjectionToken('api-communication-address');

/**
 * Maps [Partial<CommunicationAddress>]{@link CommunicationAddress#source} to
 * [CommunicationAddressResponsePatchRequest]{@link CommunicationAddressResponsePatchRequest#source}
 *
 * # <span style="color:red">**NOT EXPOSED BY PUBLIC_API!**</span>
 */
class CommunicationAddressPatchRequestMapper {
    map(address) {
        return {
            communication_address: {
                account_id: address.accountId,
                street: address.street,
                house_number: address.houseNumber,
                house_number_extension: address.houseNumberExtension,
                pobox: address.pobox,
                postcode: address.postcode,
                city: address.city,
                country: address.country,
                care_of_fullname: address.careOfFullname,
                telephone_number: address.phone,
                telephone_number_alternative: address.alternativePhone,
                email_address: address.email
            }
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CommunicationAddressPatchRequestMapper, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CommunicationAddressPatchRequestMapper }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CommunicationAddressPatchRequestMapper, decorators: [{
            type: Injectable
        }] });

class PatchCommunicationAddressService {
    constructor(apiUrl, httpClient, communicationAddressMapper) {
        this.apiUrl = apiUrl;
        this.httpClient = httpClient;
        this.communicationAddressMapper = communicationAddressMapper;
        this.apiVersion = '2';
        this.endpoint = ({ accountId }) => `${this.apiUrl || ''}/accounts/${accountId}/communicationAddresses/v${this.apiVersion}`;
    }
    /**
     * Performs a PATCH request to `/accounts/{id}/communicationAddresses/v2`.
     */
    call$(payload) {
        const { address } = payload;
        const body = this.communicationAddressMapper.map(address);
        return this.httpClient.patch(this.endpoint(payload), body);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: PatchCommunicationAddressService, deps: [{ token: API, optional: true }, { token: i1.HttpClient }, { token: CommunicationAddressPatchRequestMapper }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: PatchCommunicationAddressService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: PatchCommunicationAddressService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [API]
                }] }, { type: i1.HttpClient }, { type: CommunicationAddressPatchRequestMapper }] });

const patchCommunicationAddressApi = createNamedApi('[CommunicationAddressState] Patch communication address', PatchCommunicationAddressService);

const { EFFECTS, PROVIDERS } = combineEffectsWithProviders(patchCommunicationAddressApi);
const MAPPERS = [CommunicationAddressPatchRequestMapper];
/**
 * Module containing all providers.
 *
 * ### Example usage
 *
 * #### Normal
 * ```typescript
 * providers: [
 *     CommunicationAddressCoreModule,
 *     ...
 * ]
 * ```
 *
 * #### Custom API
 *
 * ```typescript
 * providers: [
 *     CommunicationAddressCoreModule.forRoot({
 *         api: 'https://my-api.com'
 *     }),
 *     ...
 * ]
 * ```
 */
class CommunicationAddressCoreModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('CommunicationAddressCoreModule is imported more than once.');
        }
    }
    static forRoot(config) {
        return {
            ngModule: CommunicationAddressCoreModule,
            providers: [
                {
                    provide: API,
                    useValue: config ? config.api : undefined
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CommunicationAddressCoreModule, deps: [{ token: CommunicationAddressCoreModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: CommunicationAddressCoreModule, imports: [i1$1.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CommunicationAddressCoreModule, providers: [...MAPPERS, ...PROVIDERS], imports: [EffectsModule.forFeature(EFFECTS)] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CommunicationAddressCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [EffectsModule.forFeature(EFFECTS)],
                    providers: [...MAPPERS, ...PROVIDERS]
                }]
        }], ctorParameters: () => [{ type: CommunicationAddressCoreModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }] });

const { patchCommunicationAddressActions, patchCommunicationAddress, patchCommunicationAddressSuccess, patchCommunicationAddressError, patchCommunicationAddressClear } = patchCommunicationAddressApi;

/**
 * Generated bundle index. Do not edit.
 */

export { API, CommunicationAddressCoreModule, patchCommunicationAddress, patchCommunicationAddressActions, patchCommunicationAddressClear, patchCommunicationAddressError, patchCommunicationAddressSuccess };
